import '@xyflow/react/dist/style.css'

import { MarkerType, ReactFlow } from '@xyflow/react'
import { CantileverButton } from 'components/CantileverButton'
import { PageHeader } from 'components/PageHeader'
import { useCallback, useContext, useState } from 'react'
import styled from 'styled-components'

import DashedLine from './components/edges/DashedLine'
import SolidLine from './components/edges/SolidLine'
import { CategorizedNodeLibrary } from './components/library/CategorizedNodeLibrary'
import { ProcessFlowDiagramBuilderContext } from './contexts/ProcessFlowDiagramBuilderContext'
import { createNodeTypesMapping } from './data/minimalSymbolLibrary'
import { useDragLibrary } from './hooks/useDragLibrary'
import { CommonStyles } from './svg/CommonStyles'
import { EdgeType, PfdNode } from './types'

const Container = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100vh;
  background-color: ${({ theme }) =>
    theme.forecast.pageBackground1 || theme.forecast.background1};
  color: ${({ theme }) => theme.forecast.text1};
`

const ButtonContainer = styled.div`
  display: flex;
  padding-right: 1.5rem;
  gap: 1rem;
`

const ReactFlowContainer = styled.div`
  flex: 1;
  height: 100%;
  position: relative;
`

const PopupOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
`

const PopupContainer = styled.div<{ x: number; y: number }>`
  position: fixed;
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  z-index: 1000;
  transform: translate(-50%, -100%) translateY(-12px);
  pointer-events: auto;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  background-color: ${({ theme }) => theme.forecast.background2};
`

interface ProcessFlowDiagramBuilderProps {
  viewOnly: boolean
}

export const ProcessFlowDiagramBuilder = ({
  viewOnly,
}: ProcessFlowDiagramBuilderProps) => {
  const {
    pfd,
    handleSaveToRolodex,
    onNodesChange,
    onEdgesChange,
    onConnect,
    metadata,
    disableEditing,
    setDisableEditing,
    onNodeClick,
    renderNodePopup,
  } = useContext(ProcessFlowDiagramBuilderContext)

  const { onDragOver, onDrop, onInit, reactFlowWrapper } = useDragLibrary()
  const [selectedNode, setSelectedNode] = useState<PfdNode | null>(null)
  const [popupPosition, setPopupPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleLibraryDragStart = () => {
    // Additional logic can be added here if needed
    // You can add analytics, state updates, or other side effects here
  }

  const nodeTypes = createNodeTypesMapping()
  const edgeTypes = {
    [EdgeType.SOLID_LINE]: SolidLine,
    [EdgeType.DASHED_LINE]: DashedLine,
  }

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: PfdNode) => {
      if (onNodeClick) {
        onNodeClick(node)
      }

      // Handle custom popup
      if (renderNodePopup) {
        setSelectedNode(node)

        setPopupPosition({
          x: event.clientX,
          y: event.clientY,
        })
      }
    },
    [onNodeClick, renderNodePopup]
  )

  const handleClosePopup = useCallback(() => {
    setSelectedNode(null)
    setPopupPosition(null)
  }, [])

  const handlePaneClick = useCallback(() => {
    if (selectedNode) {
      handleClosePopup()
    }
  }, [selectedNode, handleClosePopup])

  return (
    <>
      <HeaderContainer>
        <PageHeader
          title={metadata.name}
          trailingContent={
            !viewOnly && (
              <ButtonContainer>
                <CantileverButton
                  onClick={() => setDisableEditing(!disableEditing)}>
                  {disableEditing ? 'Enable Editing' : 'Disable Editing'}
                </CantileverButton>
                <CantileverButton onClick={handleSaveToRolodex} primary>
                  Save
                </CantileverButton>
              </ButtonContainer>
            )
          }
        />
      </HeaderContainer>

      <Container>
        {!viewOnly && (
          <CategorizedNodeLibrary onDragStart={handleLibraryDragStart} />
        )}
        <ReactFlowContainer ref={reactFlowWrapper}>
          <ReactFlow
            nodes={pfd.nodes}
            edges={pfd.edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onInit={onInit}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{
              type: 'solidLine',
              markerEnd: { type: MarkerType.Arrow },
            }}
            fitView>
            <CommonStyles />
          </ReactFlow>
        </ReactFlowContainer>

        {/* Custom Popup Overlay */}
        {selectedNode && renderNodePopup && popupPosition && (
          <PopupOverlay>
            <PopupContainer x={popupPosition.x} y={popupPosition.y}>
              {renderNodePopup(selectedNode, handleClosePopup)}
            </PopupContainer>
          </PopupOverlay>
        )}
      </Container>
    </>
  )
}
